/**
 * CSS2D/CSS3D katmanlarini cizen sistem (renderTargets faz).
 *
 * Yalnizca bir guncelleme istendiginde (engine.pendingRequestUpdate) ve ilgili
 * CSS nesnesi sahnede varken render eder; aksi halde DOM katmanini cikarir.
 * Kare basina bir kez pending bayragini tuketir (clearRequestUpdate).
 *
 * Not: cssRenderer / cssRendererVisible / cssRendererAdded alanlari Engine'de
 * public kalir (EngineResizeController ve interaction onlari paylasir).
 */
import type { Object3D } from "three";
import { CSS2DRenderer } from "../renderer/CSS2DRenderer";
import { CSS3DRenderer } from "../renderer/CSS3DRenderer.js";
import { noteCss2dRender, noteCss3dRender } from "../debug/RenderTrace";
import type { EngineSystem, FrameContext, FramePhase } from "../runtime/EngineSystem";

export class CssRenderSystem implements EngineSystem {
  readonly name = "cssRender";
  readonly labels: Partial<Record<FramePhase, string>> = {
    renderTargets: "renderTargets.css",
  };

  renderTargets(ctx: FrameContext): void {
    const engine = ctx.engine;
    const pending = engine.pendingRequestUpdate;

    if (engine.cssRenderer instanceof CSS2DRenderer && pending) {
      if (
        this._has(engine.activeScene!, (el) => (el as any).isCSS2DObject) &&
        engine.cssRendererVisible
      ) {
        engine.cssRenderer.domElement.classList.add("css-renderer");
        engine.cssRenderer.render(
          engine.activeScene!,
          engine.activeCamera!.instance,
        );
        noteCss2dRender();
        if (!engine.cssRendererAdded) {
          engine.bindedElement?.appendChild(engine.cssRenderer.domElement);
          engine.cssRendererAdded = true;
        }
      } else {
        engine.cssRenderer.domElement.remove();
        engine.cssRendererAdded = false;
      }
    }

    if (engine.cssRenderer3d instanceof CSS3DRenderer && pending) {
      if (
        this._has(
          engine.activeScene!,
          (el) => (el as any).isCSS3DObject && el.visible,
        ) &&
        engine.cssRendererVisible
      ) {
        engine.cssRenderer3d.domElement.classList.add("css-renderer");
        engine.cssRenderer3d.render(
          engine.activeScene!,
          engine.activeCamera!.instance,
        );
        noteCss3dRender();
        if (!engine.cssRenderer3dAdded) {
          engine.bindedElement?.appendChild(engine.cssRenderer3d.domElement);
          engine.cssRenderer3dAdded = true;
        }
      } else {
        engine.cssRenderer3d.domElement.remove();
        engine.cssRenderer3dAdded = false;
      }
    }

    // Pending bayragini kare basina bir kez tuket (offscreen RT'lerden once).
    engine.clearRequestUpdate();
  }

  /** Sahne agacinda predicate'i saglayan ilk dugum var mi (erken cikis). */
  private _has(root: Object3D, pred: (o: Object3D) => boolean): boolean {
    if (pred(root)) return true;
    for (const c of root.children) if (this._has(c, pred)) return true;
    return false;
  }
}
